import { useState } from "react";

import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { ApiError, apiFetch } from "@/lib/api";
import type { MunicipalityResource } from "@/lib/api-types";

/**
 * The five §5.5 checklist questions (Story 5.2), extracted from
 * `MunicipalityRecord.tsx` unchanged in behavior (Story 5.5, Code Map) so
 * both `MunicipalityRecord.tsx` and the Add Property wizard's own "create a
 * new town" sub-flow render the exact same component — never a second,
 * independent copy of these five questions anywhere (Boundaries &
 * Constraints, VC-5.5-07).
 *
 * The five questions map onto six real `Municipality` columns — one field
 * each for the first, second, third and fifth questions (`rent_control_cap`,
 * `co_inspection_rule`, `registration_office`, `water_sewer_biller`, each a
 * free-text description covering the whole question) and two for the fourth
 * (`tax_portal_url` + `tax_lookup_format`).
 *
 * `onComplete` fires once `PATCH /municipalities/{id}` (every answered
 * field, plus `setup_complete: true`, in one call) succeeds, handing the
 * caller the updated record. `onSkip`, when supplied, renders a second
 * "Skip for now" affordance beside "Mark setup complete" that dismisses the
 * checklist without writing anything (Story 5.5's judgement call 4: the
 * wizard never requires the checklist to be completed before the property
 * write) — `MunicipalityRecord.tsx` passes no `onSkip` at all, since there
 * is nowhere for that page to "skip" to and no behavior change is wanted
 * there (Story 5.5, Code Map): the checklist simply keeps rendering, as it
 * always has, until it is genuinely completed.
 */

const CHECKLIST_QUESTIONS: {
  key: keyof MunicipalityResource & string;
  question: string;
  helper?: string;
}[] = [
  { key: "rent_control_cap", question: "Does this town have a rent control ordinance, and if so, what is the annual cap?" },
  { key: "co_inspection_rule", question: "Does this town require a continued-occupancy or CO inspection between tenancies?" },
  { key: "registration_office", question: "Where does a landlord file the annual registration, and what triggers renewal?" },
  { key: "tax_portal_url", question: "What is this town's own tax portal?" },
  { key: "tax_lookup_format", question: "What lookup key does that portal use (e.g. block/lot/qualifier)?" },
  { key: "water_sewer_biller", question: "Who bills water/sewer, and on what cycle?" },
];

const CHECKLIST_KEYS = new Set(CHECKLIST_QUESTIONS.map((q) => q.key));

export interface MunicipalitySetupChecklistProps {
  municipality: MunicipalityResource;
  /** `PATCH /municipalities/{id}` is `role=Role.OWNER` — every write
   * affordance below is withheld from anyone but a confirmed Owner (Tier A
   * review, Story 5.2, Fix 2), not merely "not Read-only". */
  isOwner: boolean;
  onComplete: (updated: MunicipalityResource) => void;
  onSkip?: () => void;
}

export function MunicipalitySetupChecklist({ municipality, isOwner, onComplete, onSkip }: MunicipalitySetupChecklistProps) {
  const [checklistValues, setChecklistValues] = useState<Record<string, string>>({});
  const [checklistError, setChecklistError] = useState<string | undefined>(undefined);
  const [isSubmittingChecklist, setIsSubmittingChecklist] = useState(false);

  async function submitChecklist() {
    setIsSubmittingChecklist(true);
    setChecklistError(undefined);
    try {
      const body: Record<string, unknown> = { setup_complete: true };
      for (const question of CHECKLIST_QUESTIONS) {
        const typed = checklistValues[question.key]?.trim() ?? "";
        body[question.key] = typed === "" ? null : typed;
      }
      const updated = await apiFetch<MunicipalityResource>(`/municipalities/${municipality.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      onComplete(updated);
    } catch (error) {
      setChecklistError(error instanceof ApiError ? error.message : "Something went wrong. Please try again in a moment.");
    } finally {
      setIsSubmittingChecklist(false);
    }
  }

  return (
    <div className="mb-6 rounded-lg border border-border-strong bg-surface p-6">
      <h2 className="text-title font-semibold tracking-title text-ink">Finish setting up this town</h2>
      <p className="mt-1 text-body text-ink-secondary">
        Answer these once, and PropDesk stops asking for {municipality.name}.
      </p>
      <div className="mt-4 flex flex-col gap-4">
        {CHECKLIST_QUESTIONS.map((question) => (
          <label key={question.key} className="flex flex-col gap-1">
            <span className="text-label font-medium text-ink-secondary">{question.question}</span>
            <Input
              value={checklistValues[question.key] ?? ""}
              disabled={!isOwner}
              onChange={(event) =>
                setChecklistValues((existing) => ({ ...existing, [question.key]: event.target.value }))
              }
            />
          </label>
        ))}
      </div>
      {checklistError ? (
        <p role="alert" className="mt-3 text-meta text-danger">
          {checklistError}
        </p>
      ) : null}
      {isOwner ? (
        <div className="mt-4 flex gap-2">
          <Button type="button" onClick={() => void submitChecklist()} disabled={isSubmittingChecklist}>
            {isSubmittingChecklist ? "Saving…" : "Mark setup complete"}
          </Button>
          {onSkip ? (
            <Button type="button" variant="ghost" onClick={onSkip} disabled={isSubmittingChecklist}>
              Skip for now
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export { CHECKLIST_KEYS, CHECKLIST_QUESTIONS };
